import React, { useCallback, useState } from 'react'
import { RouteComponentProps } from 'react-router-dom'
import { endOfDay, startOfDay, format } from 'date-fns'
import {
  useInvoices,
  TypeFilter,
  useInvoicesLazy,
  Invoices_getInvoices_invoices,
  Invoices,
  ERRORS,
} from '@modmd/data'
import papa from 'papaparse'
import DashboardPage from 'internal-portal/sharedComponents/DashboardPage'
import Datalist from 'components/Datalist'
import Card from 'components/Card'
import { Box, Container } from 'components/Layout'
import { useSearchState } from 'internal-portal/utils/useSearchState'
import { DateInput } from 'components/DateInput'
import { SelectInput } from 'components/SelectInput'
import { FinancesDatalist } from 'sharedComponents/FinancesDatalist'
import Button from 'components/Button'
import { Spinner } from 'components/Spinner'
import { useToaster } from 'utils/useToaster'
import { TYPE_FILTER_OPTIONS } from '../TestSessions/utils'
import { invoiceDateOrFail } from './utils'

export const USERS_PAGE_LENGTH = 10

export enum FormName {
  DATE_FROM = 'dateFrom',
  DATE_TO = 'dateTo',
  TYPE_FILTER = 'typeFilter',
}

const FinancesList: React.VFC<RouteComponentProps> = () => {
  const { setToastMessage } = useToaster()
  const today = new Date()
  const lastWeek = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7)
  const searchState = useSearchState<{
    [FormName.DATE_TO]?: string | null
    [FormName.DATE_FROM]?: string | null
    [FormName.TYPE_FILTER]: TypeFilter
  }>()
  const [download, setDownload] = useState(false)

  const generateCsv = (data: object[]) => {
    const csv = papa.unparse(data, {
      header: true,
    })

    return new Blob([csv], { type: 'text/csv' })
  }

  const createDownloadableLink = (blob: Blob, fileName: string) => {
    const link = document.createElement('a')
    link.href = window.URL.createObjectURL(blob)
    link.setAttribute('download', `${fileName}.csv`)
    document.body.appendChild(link)

    const click = () => {
      link.click()
      document.body.removeChild(link)
    }

    return { link, click }
  }

  const [testSessionsPage, setTestSessionsPage] = React.useState(1)
  const { data: invoices, loading: loadingInvoices } = useInvoices({
    filter: {
      invoicedBetween: [searchState.data.dateFrom as string, searchState.data.dateTo as string],
      typeFilter: searchState.data.typeFilter,
    },
    page: testSessionsPage,
  })

  const parseResultIntoCSVModel = (invoices: Invoices_getInvoices_invoices[] | null) =>
    invoices?.map((invoice) => {
      const {
        testSessionId,
        eventId,
        TestSession,
        Event,
        invoiceDocNumber,
        invoiceDate,
        autoInvoice,
        invoiceId,
        count,
        totalPrice,
        error,
      } = invoice
      return {
        id: testSessionId ?? eventId,
        'Test Session / Event name': TestSession?.name ?? Event?.name,
        'Invoice id': invoiceId ?? 'Fail',
        'Invoice NO': invoiceId ? invoiceDocNumber : 'Fail',
        'Invoiced at': invoiceDateOrFail(invoiceDate),
        testType: TestSession?.type ?? Event?.testType,
        'Tested patients': count,
        rate: totalPrice && count ? totalPrice / count : 0,
        total: totalPrice ?? 0,
        'Rate type': TestSession?.sublocation ?? Event?.sublocation,
        process: autoInvoice ? 'Automatic' : 'Manual',
        zip: testSessionId ? TestSession?.Location?.zip : Event?.Address?.zip ?? '',
        Address: testSessionId
          ? `${TestSession?.Location?.street ?? ''}, ${TestSession?.Location?.city ?? ''} - ${
              TestSession?.Location?.state ?? ''
            }`
          : `${Event?.Address?.street ?? ''}, ${Event?.Address?.city ?? ''} - ${
              Event?.Address?.state ?? ''
            }`,
        errorMessage: error ? ERRORS.graphQlErrors[error] : error,
      }
    })

  const handleDownloadCsv = (result: Invoices) => {
    if (invoices?.getInvoices.invoices && invoices?.getInvoices.invoices.length < 1) {
      setToastMessage('No results found', 'danger')

      return
    }

    const parsedResult = parseResultIntoCSVModel(result?.getInvoices.invoices)
    const dataBlob = generateCsv(parsedResult!)

    if (download)
      createDownloadableLink(
        dataBlob,
        `Invoices_From_${
          searchState.data.dateFrom
            ? `${format(new Date(searchState.data.dateFrom), 'MM-dd-yyyy')}`
            : `${format(new Date(lastWeek), 'MM-dd-yyyy')}`
        }_To_${
          searchState.data.dateTo
            ? `${format(new Date(searchState.data.dateTo), 'MM-dd-yyyy')}`
            : `${format(new Date(today), 'MM-dd-yyyy')}`
        }`
      ).click()
    setDownload(false)
  }

  const [getInvoices, { loading: lazyInvoicesLoading }] = useInvoicesLazy({
    onCompleted: handleDownloadCsv,
  })

  const handleGetInvoices = useCallback(() => {
    getInvoices({
      variables: {
        filterData: {
          invoicedBetween: [searchState.data.dateFrom as string, searchState.data.dateTo as string],
          typeFilter: searchState.data.typeFilter,
        },
        pagination: {
          from: 0,
          length: invoices?.getInvoices?.pagination?.total as number,
        },
      },
    })
  }, [searchState, getInvoices, invoices])

  return (
    <DashboardPage>
      <Container>
        <Card>
          <FinancesDatalist
            tableHeader={
              <Datalist.Title
                rightContent={
                  <>
                    <Box
                      display="grid"
                      gridGap="1rem"
                      alignItems="center"
                      gridTemplateColumns={{
                        TABLET: 'repeat(3, 1fr)',
                        DESKTOP: '0.7fr 0.7fr 0.7fr 0.35fr',
                      }}
                    >
                      <SelectInput
                        label="Testing Type"
                        name={FormName.TYPE_FILTER}
                        value={searchState.data.typeFilter}
                        onChange={(e) => {
                          searchState.setSearchState(
                            {
                              [FormName.TYPE_FILTER]: e.target.value || null,
                              page: 1,
                            },
                            false,
                            true
                          )
                        }}
                        options={TYPE_FILTER_OPTIONS}
                        width="10rem"
                      />
                      <DateInput
                        id="invoicedFrom"
                        name="invoicedFrom"
                        label="Invoice Date From"
                        maxDate={
                          searchState.data.dateTo ? new Date(searchState.data.dateTo) : new Date()
                        }
                        value={
                          searchState.data.dateFrom ? new Date(searchState.data.dateFrom) : lastWeek
                        }
                        onChange={(date) => {
                          searchState.setSearchState(
                            {
                              [FormName.DATE_FROM]: date ? startOfDay(date).toISOString() : null,
                              page: 1,
                            },
                            false,
                            true
                          )
                        }}
                        hasBottomContent
                        isFullWidth
                      />
                      <DateInput
                        id="invoicedTo"
                        name="invoicedTo"
                        label="Invoice Date To"
                        minDate={
                          searchState.data.dateFrom ? new Date(searchState.data.dateFrom) : lastWeek
                        }
                        value={searchState.data.dateTo ? new Date(searchState.data.dateTo) : today}
                        onChange={(date) => {
                          searchState.setSearchState(
                            {
                              [FormName.DATE_TO]: date ? endOfDay(date).toISOString() : null,
                              page: 1,
                            },
                            false,
                            true
                          )
                        }}
                        hasBottomContent
                        isFullWidth
                      />
                      <Button
                        onClick={() => {
                          setDownload(true)
                          handleGetInvoices()
                        }}
                        isFetching={lazyInvoicesLoading && download}
                        colorVariant="primary"
                      >
                        {lazyInvoicesLoading && download ? (
                          <Spinner size="small" />
                        ) : (
                          `Download csv`
                        )}
                      </Button>
                    </Box>
                  </>
                }
              >
                List of invoices
              </Datalist.Title>
            }
            columnLabels={{
              invoiceCheck: null,
              numOfPeople: null,
              company: null,
              time: null,
              status: null,
              autoInvoice: 'Process',
              invoiceDate: 'Invoice date',
            }}
            isLoading={loadingInvoices}
            invoices={invoices?.getInvoices.invoices || []}
            pagination={invoices?.getInvoices?.pagination}
            onPageChange={setTestSessionsPage}
          />
        </Card>
      </Container>
    </DashboardPage>
  )
}

export { FinancesList }
